// Texts DE/EN of the detail parity with the local app (APP-PARITY.md "Detail-Abgleich", branch feature/app-parity):
// notes, the device card, the recovery offer, export, log and the resets; the figures of an opened shot are in
// texts-daily.ts.
// Wording of the local app (firmware/frontend-orione/index.html) where the same thing is meant.
import type { Lang } from './i18n.js';

type Texts = Record<string, Record<Lang, string>>;

export const PARITY_TEXTS = {
  // ------------------------------------------------------------------ machine and care (D-G03, D-M01, D-W01–D-W05)
  // the cause as a sentence of its own (23.10.2026: "… Grund: Unterspannung." was a colon chain)
  bootWatchdog: {
    de: 'Maschine ist vor {m} min neu gestartet. Die Software hing.',
    en: 'Machine restarted {m} min ago. The software froze.',
  },
  bootBrownout: {
    de: 'Maschine ist vor {m} min neu gestartet. Die Spannung war zu niedrig.',
    en: 'Machine restarted {m} min ago. The voltage was too low.',
  },
  bfProgressLeft: {
    de: 'Backflush {c} von {n} · {p} · noch ~{s} s',
    en: 'Backflush {c} of {n} · {p} · ~{s} s left',
  },
  flushPending: {
    de: 'Spült automatisch nach {n} min am Soll.',
    en: 'Flushes automatically after {n} min at target.',
  },
  runningFor: { de: 'Läuft seit', en: 'Running for' },
  fwRolledBack: {
    de: 'Letztes Update ist nicht gestartet. Vorige Firmware läuft wieder.',
    en: 'Last update didn’t start. Previous firmware is running again.',
  },
  fwRolledBackFrom: {
    de: 'Update auf {v} ist nicht gestartet. Vorige Firmware läuft wieder.',
    en: 'Update to {v} didn’t start. Previous firmware is running again.',
  },
  noScaleGoal: { de: 'Ohne Waage: Ziel {n} s', en: 'No scale: target {n} s' },
  shotInterrupted: {
    de: 'Bezug wurde durch einen Neustart abgebrochen.',
    en: 'Shot was cut short by a restart.',
  },
  dsInterrupted: {
    de: 'Entkalken wurde durch einen Neustart unterbrochen. Bitte Tank und Gruppe prüfen und neu starten.',
    en: 'Descaling was interrupted by a restart. Please check tank and group, then start again.',
  },
  // ------------------------------------------------------------------ settings (D-E01, D-G04)
  typeValue: { de: 'Wert eingeben', en: 'type a value' },
  // ------------------------------------------------------------------ recovery (FR-52), export (W18)
  recoveryQuestion: {
    de: 'Die Maschine hat ihre Einstellungen verloren. Aus der Cloud wiederherstellen?',
    en: 'The machine has lost its settings. Restore them from the cloud?',
  },
  recoveryDetail: { de: 'Stand vom {d} · {n} Änderungen', en: 'Saved on {d} · {n} changes' },
  recoveryDetailS: {
    de: 'Stand vom {d} · {n} Änderungen, {s} davon geschützt',
    en: 'Saved on {d} · {n} changes, {s} protected',
  },
  recoverySame: {
    de: 'Stand vom {d}. Die Maschine hat schon dieselben Einstellungen.',
    en: 'Saved on {d}. The machine already has these settings.',
  },
  recoveryTake: { de: 'Wiederherstellen', en: 'Restore' },
  recoveryOk: { de: 'Schließen', en: 'Dismiss' },
  recoveryKeep: { de: 'So lassen', en: 'Keep as is' },
  recoveryOwner: {
    de: 'Nur der Besitzer kann die Einstellungen wiederherstellen.',
    en: 'Only the owner can restore the settings.',
  },
  recoveryConfirmS: {
    de: '„{l}“ ändert sich von {a} auf {b}.',
    en: '“{l}” changes from {a} to {b}.',
  },
  recoveryConfirmSTitle: { de: 'Geschützten Wert wiederherstellen?', en: 'Restore a protected value?' },
  recoverySkip: { de: 'Überspringen', en: 'Skip' },
  recoveryRefused: { de: 'Nicht wiederhergestellt. {p}', en: 'Not restored. {p}' },
  exportData: { de: 'Daten exportieren', en: 'Export data' },
  exportInfo: { de: 'Zum Weiterverwenden, nicht als Sicherung.', en: 'For use elsewhere, not as a backup.' },
  exportJson: { de: 'Alles als JSON', en: 'Everything as JSON' },
  exportCsv: { de: 'Bezüge als CSV', en: 'Shots as CSV' },
  // ------------------------------------------------------------------ diagnosis, resets (W19, W21, W22)
  sDiag: { de: 'Diagnose', en: 'Diagnostics' },
  diagInfo: { de: 'Protokoll und letzter Absturz der Maschine.', en: 'The machine’s log and last crash.' },
  diagLog: { de: 'Protokoll laden', en: 'Load the log' },
  diagCrash: { de: 'Letzter Absturz', en: 'Last crash' },
  diagAt: { de: 'Stand {t}', en: 'As of {t}' },
  diagKindLog: { de: 'Protokoll', en: 'Log' },
  diagKindCrash: { de: 'Absturzbericht', en: 'Crash report' },
  diagEmpty: { de: 'Die Maschine hat nichts geschickt.', en: 'The machine didn’t send anything.' },
  sReset: { de: 'Zurücksetzen', en: 'Reset' },
  wifiReset: { de: 'WLAN zurücksetzen', en: 'Reset Wi-Fi' },
  wifiResetTitle: { de: 'WLAN zurücksetzen?', en: 'Reset Wi-Fi?' },
  wifiResetText: {
    de: 'Maschine öffnet ein eigenes WLAN zum Einrichten. Kopplung bleibt erhalten.',
    en: 'Machine opens its own Wi-Fi for setup. It stays paired.',
  },
  factoryReset: { de: 'Werkseinstellungen', en: 'Factory reset' },
  factoryResetTitle: { de: 'Auf Werkseinstellungen zurücksetzen?', en: 'Reset to factory settings?' },
  factoryResetText: {
    de: 'Maschine wird zurückgesetzt und entkoppelt. Verlauf bleibt erhalten.',
    en: 'Machine will be reset and unpaired. History is kept.',
  },
  factoryPending: {
    de: 'Maschine wird zurückgesetzt und aus der App entfernt.',
    en: 'Machine is resetting. It’ll be removed from the app.',
  },
  resetOwnerInfo: {
    de: 'Nur der Besitzer, mit frischer Anmeldung.',
    en: 'Owner only, with a fresh sign-in.',
  },
} satisfies Texts;
